import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react'
import { createPortal } from 'react-dom'
import { format } from 'date-fns'
import { DateRange } from 'react-day-picker'
import {
    ChevronRight,
    ChevronLeft,
    Loader2,
    RefreshCw,
    AlertCircle,
    Search,
    TrendingUp,
    TrendingDown,
    Activity,
    MapPin,
    Store,
    BarChart3,
    X,
    Globe2,
    ChevronDown,
} from 'lucide-react'
import { Button } from '@/components/ui/button'
import { cn } from '@/lib/utils'
import { Skeleton } from '@/components/ui/skeleton'
import { AreaChart, Area, ResponsiveContainer, YAxis, Tooltip } from 'recharts'
import { useVisibility } from '@/hooks/useVisibility'
import { useTopBar } from '@/contexts/TopBarContext'
import { TopBarFilterDropdown } from '@/components/ui/TopBarFilterDropdown'
import { DateRangePicker } from '@/components/ui/DateRangePicker'
import type {
    SparklineDataPoint,
    SOVMetric,
    CategoryVisibility,
    CityVisibility,
    BrandInsight,
    VisibilityDrilldownResponse,
    VisibilityCategoryGeoSnapshotResponse,
    VisibilityGeoSnapshotResponse,
    VisibilityGeoNode,
    KeywordSOV,
    CustomDateRange,
} from '@/types'

// ============================================================================
// CONSTANTS & STYLE PRIMITIVES
// ============================================================================

const PLATFORM_OPTIONS = [
    { value: 'all', label: 'All Platforms' },
    { value: 'blinkit', label: 'Blinkit' },
    { value: 'zepto', label: 'Zepto' },
    { value: 'instamart', label: 'Instamart' },
    { value: 'bbnow', label: 'BBNow' },
]



const GLASS_CARD = [
    "rounded-2xl",
    "bg-white/60 dark:bg-white/[0.08]",
    "backdrop-blur-xl backdrop-saturate-[1.4]",
    "border border-white/50 dark:border-white/[0.12]",
    "shadow-[0_2px_16px_rgba(0,0,0,0.06),inset_0_1px_0_0_rgba(255,255,255,0.6)]",
    "dark:shadow-[0_2px_16px_rgba(0,0,0,0.3),inset_0_1px_0_0_rgba(255,255,255,0.08)]",
    "transition-shadow duration-200 ease-out",
].join(' ')

const GLASS_HOVER = [
    "hover:shadow-[0_8px_30px_rgba(0,0,0,0.1),inset_0_1px_0_0_rgba(255,255,255,0.7)]",
    "hover:border-primary/20",
].join(' ')

const GLASS_DIVIDER = "h-px bg-gradient-to-r from-transparent via-black/[0.06] dark:via-white/[0.08] to-transparent"
const GLASS_INNER = "rounded-xl bg-white/40 dark:bg-white/[0.06] border border-white/40 dark:border-white/[0.08]"

// ============================================================================
// SMALL UI PRIMITIVES
// ============================================================================

function MetricValue({ value, size = 'lg', positiveColor = true }: { value: number; size?: 'sm' | 'lg', positiveColor?: boolean }) {
    return (
        <span className={cn(
            "font-bold leading-none",
            size === 'lg' ? "text-lg sm:text-2xl" : "text-sm sm:text-base",
            positiveColor
                ? (value >= 50 ? "text-emerald-700 dark:text-emerald-400" : value >= 20 ? "text-amber-700 dark:text-amber-400" : "text-red-700 dark:text-red-400")
                : "text-foreground"
        )}>
            {value.toFixed(2)}%
        </span>
    )
}

function TrendBadge({ value, compact = false }: { value: number; compact?: boolean }) {
    const isPositive = value >= 0
    return (
        <div className={cn(
            "inline-flex items-center rounded-md font-bold whitespace-nowrap shrink-0",
            compact ? "gap-0.5 px-1.5 py-0.5 text-[10px]" : "gap-1 px-2 py-0.5 text-[11px]",
            isPositive ? "bg-emerald-500/10 text-emerald-700 dark:text-emerald-400" : "bg-red-500/10 text-red-700 dark:text-red-400"
        )}>
            {isPositive ? <TrendingUp className={compact ? "h-2.5 w-2.5" : "h-3 w-3"} /> : <TrendingDown className={compact ? "h-2.5 w-2.5" : "h-3 w-3"} />}
            <span>{isPositive ? '+' : ''}{value.toFixed(2)}%</span>
        </div>
    )
}

// ============================================================================
// REUSABLE COMPONENTS
// ============================================================================

function SliderCarousel({ children, className }: { children: React.ReactNode; className?: string }) {
    const scrollRef = useRef<HTMLDivElement>(null)
    const [canScrollLeft, setCanScrollLeft] = useState(false)
    const [canScrollRight, setCanScrollRight] = useState(false)

    const checkScroll = useCallback(() => {
        const el = scrollRef.current
        if (!el) return
        setCanScrollLeft(el.scrollLeft > 4)
        setCanScrollRight(el.scrollLeft < el.scrollWidth - el.clientWidth - 4)
    }, [])

    useEffect(() => {
        const el = scrollRef.current
        if (!el) return
        checkScroll()
        el.addEventListener('scroll', checkScroll, { passive: true })
        const ro = new ResizeObserver(checkScroll)
        ro.observe(el)
        return () => { el.removeEventListener('scroll', checkScroll); ro.disconnect() }
    }, [checkScroll])

    useEffect(() => { checkScroll() }, [children, checkScroll])

    const scroll = (direction: 'left' | 'right') => {
        const el = scrollRef.current
        if (!el) return
        const scrollAmount = el.clientWidth * 0.7
        el.scrollBy({ left: direction === 'left' ? -scrollAmount : scrollAmount, behavior: 'smooth' })
    }

    return (
        <div className="relative group/slider">
            {canScrollLeft && (
                <button
                    onClick={() => scroll('left')}
                    className="absolute left-0 top-1/2 -translate-y-1/2 z-20 w-10 h-10 rounded-full bg-background/90 backdrop-blur-sm border border-border/60 shadow-lg flex items-center justify-center text-foreground hover:bg-background transition-opacity duration-150 -ml-3 opacity-0 group-hover/slider:opacity-100"
                >
                    <ChevronLeft className="h-5 w-5" />
                </button>
            )}

            <div
                ref={scrollRef}
                className={cn("flex overflow-x-auto scrollbar-none scroll-smooth", className)}
                style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
            >
                {children}
            </div>

            {canScrollRight && (
                <button
                    onClick={() => scroll('right')}
                    className="absolute right-0 top-1/2 -translate-y-1/2 z-20 w-10 h-10 rounded-full bg-background/90 backdrop-blur-sm border border-border/60 shadow-lg flex items-center justify-center text-foreground hover:bg-background transition-opacity duration-150 -mr-3 opacity-0 group-hover/slider:opacity-100"
                >
                    <ChevronRight className="h-5 w-5" />
                </button>
            )}

            {canScrollLeft && (
                <div className="absolute left-0 top-0 bottom-0 w-12 bg-gradient-to-r from-[#FDFDFD] dark:from-[#0A0A0A] to-transparent pointer-events-none z-10" />
            )}
            {canScrollRight && (
                <div className="absolute right-0 top-0 bottom-0 w-12 bg-gradient-to-l from-[#FDFDFD] dark:from-[#0A0A0A] to-transparent pointer-events-none z-10" />
            )}
        </div>
    )
}

function MiniSparkline({ data, color = "hsl(var(--primary))" }: { data: SparklineDataPoint[]; color?: string }) {
    if (!data || data.length === 0) return <Skeleton className="h-full w-full" />

    const yMin = Math.min(...data.map(d => d.value)) * 0.95
    const yMax = Math.max(...data.map(d => d.value)) * 1.05
    const gradientId = `colorFill-${Math.random().toString(36).substr(2, 9)}`

    return (
        <div className="h-full w-full">
            <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={data} margin={{ top: 5, right: 0, left: 0, bottom: 0 }}>
                    <defs>
                        <linearGradient id={gradientId} x1="0" y1="0" x2="0" y2="1">
                            <stop offset="5%" stopColor={color} stopOpacity={0.4} />
                            <stop offset="95%" stopColor={color} stopOpacity={0} />
                        </linearGradient>
                    </defs>
                    <YAxis domain={[yMin, yMax]} hide />
                    <Tooltip
                        cursor={{ stroke: color, strokeWidth: 1, strokeDasharray: "4 4" }}
                        content={({ active, payload }) => {
                            if (active && payload && payload.length) {
                                return (
                                    <div className="bg-background/95 backdrop-blur border border-border rounded-lg shadow-md px-2 py-1.5 text-xs font-bold text-foreground">
                                        {(payload[0].value as number).toFixed(2)}%
                                    </div>
                                )
                            }
                            return null
                        }}
                    />
                    <Area
                        type="monotone"
                        dataKey="value"
                        stroke={color}
                        strokeWidth={2.5}
                        fillOpacity={1}
                        fill={`url(#${gradientId})`}
                        isAnimationActive={true}
                    />
                </AreaChart>
            </ResponsiveContainer>
        </div>
    )
}

// ============================================================================
// MODULE CARDS
// ============================================================================

function SOVMetricCard({ title, metric, type }: { title: string; metric: SOVMetric; type: 'overall' | 'organic' | 'ad' }) {
    const cardConfig = type === 'overall'
        ? {
            color: "hsl(var(--primary))",
            iconWrapClass: "bg-primary/10 text-primary",
            icon: <Activity className="h-4 w-4" />,
        }
        : type === 'organic'
            ? {
                color: "hsl(var(--emerald-500))",
                iconWrapClass: "bg-emerald-500/10 text-emerald-500",
                icon: <TrendingUp className="h-4 w-4" />,
            }
            : {
                color: "hsl(var(--amber-500))",
                iconWrapClass: "bg-amber-500/10 text-amber-500",
                icon: <Search className="h-4 w-4" />,
            }

    return (
        <div className={cn("flex-1 p-5 sm:p-6", GLASS_CARD, GLASS_HOVER)}>
            <div className="flex items-center justify-between gap-4">
                <div className="flex-1">
                    <div className="flex items-center gap-2 mb-2">
                        <div className={cn("w-8 h-8 rounded-lg flex items-center justify-center", cardConfig.iconWrapClass)}>
                            {cardConfig.icon}
                        </div>
                        <h3 className="font-semibold text-muted-foreground tracking-tight">{title}</h3>
                    </div>
                    <div className="flex items-end gap-3 mt-4">
                        <span className="text-4xl font-bold tracking-tight text-foreground">
                            {(metric.value ?? 0).toFixed(2)}%
                        </span>
                        <div className="mb-1.5">
                            <TrendBadge value={metric.change ?? 0} />
                        </div>
                    </div>
                </div>
                {/* Graph Space */}
                <div className="w-[180px] h-[80px] hidden sm:block">
                    <MiniSparkline data={metric.sparkline} color={cardConfig.color} />
                </div>
            </div>
        </div>
    )
}

function CategoryCard({
    category,
    isSelected,
    onSelect,
    onDrilldown,
}: {
    category: CategoryVisibility
    isSelected: boolean
    onSelect: () => void
    onDrilldown: () => void
}) {
    const isAllCat = category.name === 'All Categories' || category.name === 'All'
    return (
        <div
            onClick={onSelect}
            className={cn(
                "group/card w-full cursor-pointer",
                GLASS_CARD,
                GLASS_HOVER,
                isSelected && "ring-2 ring-primary/60 border-primary/30 shadow-[0_0_0_2px_hsl(var(--primary)/0.2)]"
            )}
        >
            <div className="px-5 pt-5 pb-0 flex flex-col justify-between min-h-[160px]">
                <div>
                    <div className="flex items-center gap-2.5 mb-2">
                        <div className={cn(
                            "w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0",
                            isSelected ? "bg-primary text-primary-foreground" : "bg-primary/10"
                        )}>
                            {isAllCat
                                ? <Globe2 className={cn("h-4 w-4", isSelected ? "text-primary-foreground" : "text-primary")} />
                                : <BarChart3 className={cn("h-4 w-4", isSelected ? "text-primary-foreground" : "text-primary")} />}
                        </div>
                        <h3 className="font-semibold text-foreground text-lg leading-tight truncate">{category.name}</h3>
                        {isSelected && (
                            <span className="ml-auto text-[10px] font-bold uppercase tracking-wider text-primary bg-primary/10 px-2 py-0.5 rounded-md flex-shrink-0">Active</span>
                        )}
                    </div>
                </div>

                <div className="grid grid-cols-2 gap-4 mt-4">
                    <div className={cn("p-2.5 rounded-xl min-w-0 overflow-hidden", GLASS_INNER)}>
                        <div className="flex flex-wrap items-center justify-between gap-1.5 mb-1">
                            <span className="text-[11px] text-muted-foreground font-semibold uppercase tracking-wider leading-tight">Overall SOV</span>
                            <TrendBadge value={category.changeVsPrev ?? 0} compact />
                        </div>
                        <MetricValue value={category.overallSOV ?? 0} size="sm" />
                    </div>
                    <div className={cn("p-2.5 rounded-xl min-w-0 overflow-hidden", GLASS_INNER)}>
                        <div className="flex flex-wrap items-center justify-between gap-1.5 mb-1">
                            <span className="text-[11px] text-muted-foreground font-semibold uppercase tracking-wider leading-tight">Ad SOV</span>
                            <TrendBadge value={category.changeVsPrev ?? 0} compact />
                        </div>
                        <MetricValue value={category.adSOV ?? 0} size="sm" positiveColor={false} />
                    </div>
                </div>
            </div>

            <button
                onClick={(event) => {
                    event.stopPropagation()
                    onDrilldown()
                }}
                className={cn(
                    "w-[calc(100%-2.5rem)] mx-5 mb-4 mt-4",
                    "group/footer flex items-center justify-between px-3 py-2 rounded-xl relative overflow-hidden",
                    "bg-primary/[0.04] dark:bg-primary/[0.08] border border-primary/10 dark:border-primary/15",
                    "transition-colors duration-200 ease-out",
                    "group-hover/card:bg-primary/[0.07] group-hover/card:border-primary/20 hover:!bg-primary/[0.14] hover:shadow-sm"
                )}
            >
                <div className="absolute inset-0 bg-gradient-to-r from-primary/[0.05] via-primary/[0.08] to-transparent opacity-0 transition-opacity duration-200 group-hover/footer:opacity-100 pointer-events-none" />
                <span className="relative text-xs font-semibold uppercase tracking-wider text-muted-foreground/80 group-hover/card:text-primary">
                    Show Drilldown
                </span>
                <ChevronRight className="relative h-4 w-4 text-muted-foreground/60 group-hover/card:text-primary transition-transform group-hover/footer:translate-x-0.5" />
            </button>
        </div>
    )
}

function AllCitiesCard({
    overallSOV,
    adSOV,
    overallChange,
    adChange,
    onChange,
    onDrilldown,
    isSelected,
}: {
    overallSOV: number
    adSOV: number
    overallChange: number
    adChange: number
    isSelected: boolean
    onChange: () => void
    onDrilldown: () => void
}) {
    return (
        <div
            onClick={onChange}
            className={cn(
                "group/card relative overflow-hidden cursor-pointer",
                GLASS_CARD,
                GLASS_HOVER,
                isSelected && "ring-2 ring-primary/60 border-primary/30"
            )}
        >
            <div className="relative z-[1]">
                <div className="p-4 pb-3">
                    <div className="flex gap-3 items-center">
                        <div className={cn(
                            "w-10 h-10 rounded-lg flex-shrink-0 flex items-center justify-center border shadow-sm",
                            isSelected
                                ? "bg-primary text-primary-foreground border-primary/30"
                                : "bg-white/70 dark:bg-white/10 border-white/60 dark:border-white/10"
                        )}>
                            <Globe2 className={cn("h-5 w-5", isSelected ? "text-primary-foreground" : "text-primary")} />
                        </div>
                        <div className="flex-1 min-w-0">
                            <h3 className="font-semibold text-foreground text-base leading-snug truncate">All Cities</h3>
                            {isSelected && <span className="text-[10px] font-bold text-primary uppercase tracking-wider">Active</span>}
                        </div>
                    </div>
                </div>

                <div className={cn("mx-4", GLASS_DIVIDER)} />

                <div className="p-4 pt-3">
                    <div className="flex items-stretch gap-2">
                        <div className={cn("flex-1 min-w-0 p-3 text-center overflow-hidden", GLASS_INNER)}>
                            <div className="flex flex-wrap items-center justify-between gap-1.5 mb-1.5">
                                <span className="text-xs font-semibold text-muted-foreground uppercase tracking-wider leading-tight">Overall SOV</span>
                                <TrendBadge value={overallChange ?? 0} compact />
                            </div>
                            <MetricValue value={overallSOV ?? 0} />
                        </div>
                        <div className={cn("flex-1 min-w-0 p-3 text-center overflow-hidden", GLASS_INNER)}>
                            <div className="flex flex-wrap items-center justify-between gap-1.5 mb-1.5">
                                <span className="text-xs font-semibold text-muted-foreground uppercase tracking-wider leading-tight">Ad SOV</span>
                                <TrendBadge value={adChange ?? 0} compact />
                            </div>
                            <MetricValue value={adSOV ?? 0} positiveColor={false} />
                        </div>
                    </div>
                </div>

                <button
                    onClick={(event) => {
                        event.stopPropagation()
                        onDrilldown()
                    }}
                    className={cn(
                        "group/footer mx-4 mb-3 mt-1 w-[calc(100%-2rem)] flex items-center justify-between px-3 py-2 rounded-xl relative overflow-hidden",
                        "bg-primary/[0.04] dark:bg-primary/[0.08] border border-primary/10 dark:border-primary/15",
                        "transition-colors duration-200 ease-out",
                        "group-hover/card:bg-primary/[0.07] group-hover/card:border-primary/20 hover:!bg-primary/[0.14] hover:shadow-sm"
                    )}
                >
                    <div className="absolute inset-0 bg-gradient-to-r from-primary/[0.05] via-primary/[0.08] to-transparent opacity-0 transition-opacity duration-200 group-hover/footer:opacity-100 pointer-events-none" />
                    <span className="relative text-xs font-semibold uppercase tracking-wider text-muted-foreground/80 group-hover/card:text-primary">
                        Show Drilldown
                    </span>
                    <ChevronRight className="relative h-4 w-4 text-muted-foreground/60 group-hover/card:text-primary transition-transform group-hover/footer:translate-x-0.5" />
                </button>
            </div>
        </div>
    )
}

function CityCard({ city, onDrilldown }: { city: CityVisibility; onDrilldown: () => void }) {
    return (
        <div onClick={onDrilldown} className={cn("group/card relative overflow-hidden cursor-pointer", GLASS_CARD, GLASS_HOVER)}>
            <div className="relative z-[1]">
                <div className="p-4 pb-3">
                    <div className="flex gap-3 items-center">
                        <div className="w-10 h-10 rounded-lg flex-shrink-0 flex items-center justify-center bg-white/70 dark:bg-white/10 border border-white/60 dark:border-white/10 shadow-sm">
                            <MapPin className="h-5 w-5 text-primary" />
                        </div>
                        <div className="flex-1 min-w-0">
                            <h3 className="font-semibold text-foreground text-base leading-snug truncate">{city.name}</h3>
                        </div>
                    </div>
                </div>

                <div className={cn("mx-4", GLASS_DIVIDER)} />

                <div className="p-4 pt-3">
                    <div className="flex items-stretch gap-2">
                        <div className={cn("flex-1 min-w-0 p-3 text-center overflow-hidden", GLASS_INNER)}>
                            <div className="flex flex-wrap items-center justify-between gap-1.5 mb-1.5">
                                <span className="text-xs font-semibold text-muted-foreground uppercase tracking-wider leading-tight">Overall SOV</span>
                                <TrendBadge value={city.changeVsPrev ?? 0} compact />
                            </div>
                            <MetricValue value={city.overallSOV ?? 0} />
                        </div>
                        <div className={cn("flex-1 min-w-0 p-3 text-center overflow-hidden", GLASS_INNER)}>
                            <div className="flex flex-wrap items-center justify-between gap-1.5 mb-1.5">
                                <span className="text-xs font-semibold text-muted-foreground uppercase tracking-wider leading-tight">Ad SOV</span>
                                <TrendBadge value={city.changeVsPrev ?? 0} compact />
                            </div>
                            <MetricValue value={city.adSOV ?? 0} positiveColor={false} />
                        </div>
                    </div>
                </div>

                <div className={cn(
                    "group/footer mx-4 mb-3 mt-1 flex items-center justify-between px-3 py-2 rounded-xl relative overflow-hidden",
                    "bg-primary/[0.04] dark:bg-primary/[0.08] border border-primary/10 dark:border-primary/15",
                    "transition-colors duration-200 ease-out",
                    "group-hover/card:bg-primary/[0.07] group-hover/card:border-primary/20 hover:!bg-primary/[0.14] hover:shadow-sm"
                )}>
                    <div className="absolute inset-0 bg-gradient-to-r from-primary/[0.05] via-primary/[0.08] to-transparent opacity-0 transition-opacity duration-200 group-hover/footer:opacity-100 pointer-events-none" />
                    <span className="relative text-xs font-semibold uppercase tracking-wider text-muted-foreground/80 group-hover/card:text-primary">
                        Show Drilldown
                    </span>
                    <ChevronRight className="relative h-4 w-4 text-muted-foreground/60 group-hover/card:text-primary transition-transform group-hover/footer:translate-x-0.5" />
                </div>
            </div>
        </div>
    )
}

// ============================================================================
// GEO TREE PANEL (shared by city drilldown + keyword geo)
// ============================================================================

function GeoTreePanel({
    cities,
    isSingleCity = false,
    clusters,
    expandedClusters,
    expandedPins,
    toggleCluster,
    togglePin,
}: {
    cities: VisibilityGeoNode[]
    isSingleCity?: boolean
    clusters?: VisibilityGeoNode[]
    expandedClusters: Set<string>
    expandedPins: Set<string>
    toggleCluster: (id: string) => void
    togglePin: (id: string) => void
}) {
    const formatMetric = (value: number | null) => (value === null ? '-' : `${value.toFixed(2)}%`)
    const formatPosition = (value: number | null) => (value === null ? '-' : value.toFixed(2))

    // For a single-city view, use the provided `clusters` prop (legacy path)
    // For multi-city keyword geo, iterate cities then their children
    const rows: Array<{ node: VisibilityGeoNode; depth: number }> = []
    for (const city of cities) {
        rows.push({ node: city, depth: 0 })
        const clusterNodes = isSingleCity ? (clusters ?? []) : (city.children ?? [])
        for (const cluster of clusterNodes) {
            rows.push({ node: cluster, depth: 1 })
            if (expandedClusters.has(cluster.id)) {
                for (const pin of (cluster.children ?? [])) {
                    rows.push({ node: pin, depth: 2 })
                    if (expandedPins.has(pin.id)) {
                        for (const store of (pin.children ?? [])) {
                            rows.push({ node: store, depth: 3 })
                        }
                    }
                }
            }
        }
    }

    return (
        <div className="space-y-3">
            <div className={cn("grid grid-cols-[1fr_120px_120px_120px] gap-2 items-center px-3 py-2 text-[11px] uppercase tracking-wider text-muted-foreground border-b border-border/40", GLASS_INNER)}>
                <span>City / Cluster / Pin / Store</span>
                <span className="text-right">SOV</span>
                <span className="text-right">7D Base</span>
                <span className="text-right">Avg Pos</span>
            </div>
            <div className={cn("space-y-1 p-1", GLASS_CARD)}>
                {rows.map(({ node, depth }) => {
                    const isExpandable = depth === 1 || depth === 2
                    const isExpanded = depth === 1 ? expandedClusters.has(node.id) : depth === 2 ? expandedPins.has(node.id) : false
                    const icons: Record<number, React.ReactNode> = {
                        0: <MapPin className="w-4 h-4 text-primary" />,
                        1: <Store className="h-3.5 w-3.5 text-muted-foreground" />,
                        2: <Activity className="h-3 w-3 text-muted-foreground" />,
                        3: <Store className="h-3 w-3 text-muted-foreground/60" />,
                    }
                    const content = (
                        <div className="grid grid-cols-[1fr_120px_120px_120px] gap-2 items-center px-3 py-2.5">
                            <div className={cn("flex items-center gap-2", depth === 1 && "pl-4", depth === 2 && "pl-8", depth === 3 && "pl-12")}>
                                {isExpandable && (
                                    <ChevronRight className={cn("h-3.5 w-3.5 text-muted-foreground transition-transform", isExpanded && "rotate-90")} />
                                )}
                                {icons[depth]}
                                <span className={cn("truncate", depth === 0 ? "font-semibold text-foreground" : "text-sm font-medium text-foreground/80")}>{node.name}</span>
                            </div>
                            <span className="text-right font-semibold text-sm">{formatMetric(node.currentVisibility)}</span>
                            <span className="text-right text-sm text-muted-foreground">{formatMetric(node.baseline7dVisibility)}</span>
                            <span className="text-right text-sm">{formatPosition(node.currentAvgPosition)}</span>
                        </div>
                    )
                    return isExpandable ? (
                        <button
                            key={node.id}
                            onClick={() => depth === 1 ? toggleCluster(node.id) : togglePin(node.id)}
                            className="w-full hover:bg-muted/20 rounded-md text-left border-b border-border/20 last:border-0"
                        >
                            {content}
                        </button>
                    ) : (
                        <div key={node.id} className="border-b border-border/20 last:border-0">{content}</div>
                    )
                })}
            </div>
        </div>
    )
}

function VisibilityDrilldownSidebar({
    isOpen,
    drilldownData,
    cityGeoData,
    categoryGeoData,
    keywordGeoData,
    isLoading,
    isLoadingKeywordGeo,
    drilldownMode,
    selectedCategoryName,
    selectedKeyword,
    onKeywordSelect,
    onClose
}: {
    isOpen: boolean
    drilldownData: VisibilityDrilldownResponse | null
    cityGeoData: VisibilityGeoSnapshotResponse | null
    categoryGeoData: VisibilityCategoryGeoSnapshotResponse | null
    keywordGeoData: VisibilityCategoryGeoSnapshotResponse | null
    isLoading: boolean
    isLoadingKeywordGeo?: boolean
    drilldownMode: 'category' | 'city'
    selectedCategoryName?: string
    selectedKeyword?: KeywordSOV | null
    onKeywordSelect?: (kw: KeywordSOV | null) => void
    onClose: () => void
}) {
    const [activeTab, setActiveTab] = useState<'keywords' | 'insights'>('keywords')
    const [expandedKwClusters, setExpandedKwClusters] = useState<Set<string>>(new Set())
    const [expandedKwPins, setExpandedKwPins] = useState<Set<string>>(new Set())

    useEffect(() => {
        if (isOpen) {
            setActiveTab('keywords')
        }
    }, [isOpen, drilldownMode])

    useEffect(() => {
        if (!isOpen) {
            setExpandedKwClusters(new Set())
            setExpandedKwPins(new Set())
        }
    }, [isOpen, drilldownMode, selectedKeyword?.id])

    if (!isOpen) return null

    const sidebarTitle = drilldownMode === 'city'
        ? (cityGeoData?.cityName || drilldownData?.cityFilter || 'Loading...')
        : (drilldownData?.categoryName || 'Loading...')

    return createPortal(
        <div className="fixed inset-0 z-50">
            <div className="absolute inset-0 bg-black/80 animate-in fade-in duration-300" onClick={onClose} />
            <div className="absolute right-0 top-0 h-full w-full lg:w-[65vw] max-w-4xl bg-background shadow-2xl border-l border-border/40 flex flex-col animate-in slide-in-from-right duration-400 ease-out sm:rounded-l-3xl overflow-hidden">

                {/* Header */}
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 px-6 sm:px-10 py-6 sm:py-8 border-b border-border/40 bg-card/50 backdrop-blur-xl flex-shrink-0 z-10">
                    <div className="flex items-center gap-5 flex-1 min-w-0">
                        <div className="relative">
                            <div className="absolute inset-0 bg-primary/20 blur-xl rounded-full" />
                            <div className="relative w-16 h-16 sm:w-20 sm:h-20 bg-primary/10 rounded-2xl border border-primary/20 flex items-center justify-center shadow-lg">
                                {drilldownMode === 'city' ? <MapPin className="w-8 h-8 text-primary" /> : <BarChart3 className="w-8 h-8 text-primary" />}
                            </div>
                        </div>
                        <div className="min-w-0 flex-1">
                            <span className="inline-block px-3 py-1 bg-primary/10 text-primary text-[10px] font-bold uppercase tracking-wider rounded-md mb-2">
                                Visibility Drilldown
                            </span>
                            <h3 className="text-2xl sm:text-3xl font-bold text-foreground leading-tight truncate">
                                {sidebarTitle}
                            </h3>
                            {drilldownMode === 'city' ? (
                                <p className="text-sm font-semibold text-muted-foreground mt-1.5 flex items-center gap-2">
                                    {selectedCategoryName && selectedCategoryName !== 'All Categories'
                                        ? <span>Category: <span className="text-primary">{selectedCategoryName}</span></span>
                                        : <span>All Categories</span>}
                                    {cityGeoData?.latestSnapshotAt && (
                                        <span className="opacity-60">· {new Date(cityGeoData.latestSnapshotAt).toLocaleString()}</span>
                                    )}
                                </p>
                            ) : drilldownData && (
                                <p className="text-sm font-semibold text-muted-foreground mt-1.5 flex items-center gap-2">
                                    <span>{drilldownData.cityFilter}</span>
                                    {categoryGeoData?.latestSnapshotAt && (
                                        <span className="opacity-60">· {new Date(categoryGeoData.latestSnapshotAt).toLocaleString()}</span>
                                    )}
                                </p>
                            )}
                        </div>
                    </div>
                    <Button variant="secondary" size="icon" className="rounded-xl hover:bg-destructive hover:text-destructive-foreground transition-colors h-9 w-9 flex-shrink-0 shadow-sm" onClick={onClose}>
                        <X className="h-4 w-4" />
                    </Button>
                </div>

                {/* Tabs */}
                <div className="px-6 sm:px-10 py-4 border-b border-border/40 bg-muted/10">
                    <div className="flex gap-1 p-1 bg-muted rounded-xl w-fit border border-border/50">
                        <button
                            onClick={() => setActiveTab('keywords')}
                            className={cn(
                                "px-5 py-2 text-sm font-semibold rounded-lg transition-all",
                                activeTab === 'keywords' ? "bg-background text-foreground shadow-sm ring-1 ring-border" : "text-muted-foreground hover:text-foreground"
                            )}
                        >
                            Keywords
                        </button>
                        <button
                            onClick={() => setActiveTab('insights')}
                            className={cn(
                                "px-5 py-2 text-sm font-semibold rounded-lg transition-all",
                                activeTab === 'insights' ? "bg-background text-foreground shadow-sm ring-1 ring-border" : "text-muted-foreground hover:text-foreground"
                            )}
                        >
                            Key Insights
                        </button>
                    </div>
                </div>

                {/* Content */}
                <div className="flex-1 overflow-y-auto p-6 sm:p-10 relative">
                    {isLoading ? (
                        <div className="flex flex-col items-center justify-center h-full gap-4">
                            <Loader2 className="h-8 w-8 text-primary animate-spin" />
                            <p className="text-muted-foreground font-medium">Fetching detailed metrics...</p>
                        </div>
                    ) : !drilldownData ? (
                        <div className="flex flex-col items-center justify-center h-full gap-4">
                            <AlertCircle className="h-12 w-12 text-muted-foreground/50" />
                            <p className="text-muted-foreground font-medium text-lg">Failed to load drilldown data</p>
                        </div>
                    ) : (
                        <div className="animate-in fade-in duration-300">
                            {activeTab === 'keywords' ? (
                                <div className="space-y-4">
                                    {drilldownData.keywordGroups.generic.keywords.length > 0 && (
                                        <KeywordGroupCard
                                            label="Generic Keywords"
                                            groupKey="generic"
                                            metrics={drilldownData.keywordGroups.generic}
                                            defaultExpanded
                                            selectedKeyword={selectedKeyword}
                                            onKeywordSelect={onKeywordSelect}
                                        />
                                    )}
                                    {drilldownData.keywordGroups.brand.keywords.length > 0 && (
                                        <KeywordGroupCard
                                            label="Brand Keywords"
                                            groupKey="brand"
                                            metrics={drilldownData.keywordGroups.brand}
                                            selectedKeyword={selectedKeyword}
                                            onKeywordSelect={onKeywordSelect}
                                        />
                                    )}
                                    {drilldownData.keywordGroups.competition.keywords.length > 0 && (
                                        <KeywordGroupCard
                                            label="Competitor Keywords"
                                            groupKey="competition"
                                            metrics={drilldownData.keywordGroups.competition}
                                            selectedKeyword={selectedKeyword}
                                            onKeywordSelect={onKeywordSelect}
                                        />
                                    )}
                                    {drilldownData.keywordGroups.generic.keywords.length === 0 &&
                                        drilldownData.keywordGroups.brand.keywords.length === 0 &&
                                        drilldownData.keywordGroups.competition.keywords.length === 0 && (
                                            <div className={cn("p-10 flex flex-col items-center justify-center text-muted-foreground", GLASS_CARD)}>
                                                <Search className="h-10 w-10 mb-3 opacity-30" />
                                                <p className="font-medium">No keyword data available</p>
                                            </div>
                                        )}
                                    {selectedKeyword && (
                                        <div className={cn("p-4 rounded-2xl border border-primary/20", GLASS_INNER)}>
                                            <div className="flex items-center justify-between mb-3">
                                                <div className="flex items-center gap-2">
                                                    <MapPin className="h-4 w-4 text-primary" />
                                                    <span className="font-bold text-foreground text-sm">Keyword Geo: <span className="text-primary">{selectedKeyword.keyword}</span></span>
                                                </div>
                                                {onKeywordSelect && (
                                                    <button onClick={() => onKeywordSelect(null)} className="text-xs text-muted-foreground hover:text-foreground flex items-center gap-1">
                                                        <X className="h-3 w-3" /> Close
                                                    </button>
                                                )}
                                            </div>
                                            {isLoadingKeywordGeo ? (
                                                <div className="flex items-center justify-center h-24 gap-3">
                                                    <Loader2 className="h-5 w-5 text-primary animate-spin" />
                                                    <span className="text-sm text-muted-foreground">Loading geo data...</span>
                                                </div>
                                            ) : keywordGeoData && keywordGeoData.cities.length > 0 ? (
                                                <GeoTreePanel
                                                    cities={keywordGeoData.cities}
                                                    expandedClusters={expandedKwClusters}
                                                    expandedPins={expandedKwPins}
                                                    toggleCluster={(id) => setExpandedKwClusters(prev => { const s = new Set(prev); s.has(id) ? s.delete(id) : s.add(id); return s })}
                                                    togglePin={(id) => setExpandedKwPins(prev => { const s = new Set(prev); s.has(id) ? s.delete(id) : s.add(id); return s })}
                                                />
                                            ) : (
                                                <p className="text-sm text-muted-foreground text-center py-4">No geo data found for this keyword</p>
                                            )}
                                        </div>
                                    )}
                                </div>
                            ) : (
                                <div className="space-y-8">
                                    <div>
                                        <div className="flex items-center gap-2 mb-4">
                                            <TrendingUp className="h-5 w-5 text-emerald-500" />
                                            <h3 className="text-lg font-bold text-foreground">Gainers</h3>
                                            <span className="text-sm text-muted-foreground font-medium ml-2">-- Brands gaining SOV</span>
                                        </div>
                                        <div className="space-y-3">
                                            {drilldownData.gainers.length > 0 ? (
                                                drilldownData.gainers.map((brand) => <BrandInsightCard key={brand.id} insight={brand} type="gainer" />)
                                            ) : (
                                                <p className="text-sm text-muted-foreground p-4 bg-muted/20 rounded-xl border border-dashed text-center">No gainers found</p>
                                            )}
                                        </div>
                                    </div>
                                    <div>
                                        <div className="flex items-center gap-2 mb-4">
                                            <TrendingDown className="h-5 w-5 text-red-500" />
                                            <h3 className="text-lg font-bold text-foreground">Drainers</h3>
                                            <span className="text-sm text-muted-foreground font-medium ml-2">-- Brands losing SOV</span>
                                        </div>
                                        <div className="space-y-3">
                                            {drilldownData.drainers.length > 0 ? (
                                                drilldownData.drainers.map((brand) => <BrandInsightCard key={brand.id} insight={brand} type="drainer" />)
                                            ) : (
                                                <p className="text-sm text-muted-foreground p-4 bg-muted/20 rounded-xl border border-dashed text-center">No drainers found</p>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            )}
                        </div>
                    )}

                </div>
            </div>
        </div>,
        document.body
    )
}

function BrandInsightCard({ insight, type }: { insight: BrandInsight; type: 'drainer' | 'gainer' }) {
    const isGainer = type === 'gainer'
    return (
        <div className={cn("flex flex-col sm:flex-row sm:items-center justify-between p-4 gap-4", GLASS_INNER)}>
            <div className="flex items-center gap-3">
                <div className={cn("w-2 h-2 rounded-full", isGainer ? "bg-emerald-500" : "bg-red-500")} />
                <span className="font-bold text-foreground">{insight.brandName}</span>
            </div>
            <div className="flex items-center gap-6 text-sm">
                <div className="text-right">
                    <p className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground mb-0.5">Overall SOV</p>
                    <p className="font-bold text-foreground">{(insight.currentSOV ?? 0).toFixed(2)}%</p>
                </div>
                <div className="text-right w-20">
                    <p className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground mb-0.5">Change</p>
                    <TrendBadge value={insight.change ?? 0} />
                </div>
            </div>
        </div>
    )
}

function KeywordGroupCard({
    label,
    groupKey,
    metrics,
    defaultExpanded = false,
    selectedKeyword,
    onKeywordSelect,
}: {
    label: string
    groupKey: string
    metrics: VisibilityDrilldownResponse['keywordGroups']['generic']
    defaultExpanded?: boolean
    selectedKeyword?: KeywordSOV | null
    onKeywordSelect?: (kw: KeywordSOV | null) => void
}) {
    const [isExpanded, setIsExpanded] = useState(defaultExpanded)
    const count = metrics.keywords.length

    const badgeColor =
        groupKey === 'generic' ? "bg-blue-500/10 text-blue-600 dark:text-blue-400 border-blue-500/20" :
            groupKey === 'brand' ? "bg-purple-500/10 text-purple-600 dark:text-purple-400 border-purple-500/20" :
                "bg-orange-500/10 text-orange-600 dark:text-orange-400 border-orange-500/20"

    return (
        <div className={cn("overflow-hidden", GLASS_CARD)}>
            <button
                onClick={() => setIsExpanded(!isExpanded)}
                className="w-full flex items-center justify-between p-4 hover:bg-muted/30 transition-colors"
            >
                <div className="flex items-center gap-3">
                    <span className={cn("px-2.5 py-1 rounded-lg text-[10px] font-bold uppercase tracking-wider border", badgeColor)}>
                        {groupKey}
                    </span>
                    <span className="font-bold text-foreground text-sm">{label}</span>
                    <span className="text-xs text-muted-foreground font-semibold bg-muted/50 px-2 py-0.5 rounded-md">{count} keywords</span>
                </div>
                <div className="flex items-center gap-4">
                    <div className="hidden sm:flex items-center gap-3 text-xs font-semibold text-muted-foreground">
                        <span className={GLASS_INNER + " px-2 py-0.5"}>Share: <span className="text-foreground">{(metrics.categoryImpShare ?? 0).toFixed(1)}%</span></span>
                        <span className={GLASS_INNER + " px-2 py-0.5"}>SOV: <span className="text-foreground">{(metrics.overallSOV ?? 0).toFixed(1)}%</span></span>
                    </div>
                    <ChevronDown className={cn("h-4 w-4 text-muted-foreground transition-transform duration-200", isExpanded && "rotate-180")} />
                </div>
            </button>

            {isExpanded && (
                <div className="border-t border-border/30 animate-in fade-in slide-in-from-top-1 duration-200">
                    <div className="overflow-x-auto">
                        <table className="w-full text-sm whitespace-nowrap">
                            <thead>
                                <tr className="border-b border-border/40 bg-muted/20 text-xs text-muted-foreground uppercase tracking-wider">
                                    <th className="text-left font-bold py-3 px-5">Keyword</th>
                                    <th className="text-right font-bold py-3 px-4">Cat. Imp. Share</th>
                                    <th className="text-right font-bold py-3 px-4">Overall SOV</th>
                                    <th className="text-right font-bold py-3 px-4">Organic SOV</th>
                                    <th className="text-right font-bold py-3 px-5">Ad SOV</th>
                                </tr>
                            </thead>
                            <tbody>
                                {metrics.keywords.map((keyword, idx) => {
                                    const isKwSelected = selectedKeyword?.id === keyword.id
                                    const handleRowSelect = () => {
                                        if (!onKeywordSelect) return
                                        onKeywordSelect(isKwSelected ? null : keyword as unknown as KeywordSOV)
                                    }
                                    return (
                                        <tr key={keyword.id} className={cn(
                                            "transition-colors",
                                            onKeywordSelect && "cursor-pointer",
                                            idx < metrics.keywords.length - 1 && "border-b border-border/20",
                                            isKwSelected ? "bg-primary/5" : "hover:bg-muted/20"
                                        )}
                                            onClick={onKeywordSelect ? handleRowSelect : undefined}
                                        >
                                            <td className="py-2.5 px-5 font-medium text-foreground">{keyword.keyword}</td>
                                            <td className="py-2.5 px-4 text-right text-muted-foreground tabular-nums">{(keyword.categoryImpShare ?? 0).toFixed(2)}%</td>
                                            <td className="py-2.5 px-4 text-right font-bold text-foreground tabular-nums">{(keyword.overallSOV ?? 0).toFixed(2)}%</td>
                                            <td className="py-2.5 px-4 text-right text-muted-foreground tabular-nums">{(keyword.organicSOV ?? 0).toFixed(2)}%</td>
                                            <td className="py-2.5 px-5 text-right text-muted-foreground tabular-nums">{(keyword.adSOV ?? 0).toFixed(2)}%</td>
                                        </tr>
                                    )
                                })}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}
        </div>
    )
}

// ============================================================================
// SKELETONS & STATES
// ============================================================================

function CategoryCardSkeleton() {
    return (
        <div className={cn("w-[320px] p-5 min-h-[160px] flex flex-col justify-between", GLASS_CARD)}>
            <div>
                <div className="flex items-center gap-2.5 mb-4">
                    <Skeleton className="w-8 h-8 rounded-lg" />
                    <Skeleton className="h-5 w-28" />
                </div>
                <div className="grid grid-cols-2 gap-4 mt-2">
                    <div className="space-y-2"><Skeleton className="h-3 w-16" /><Skeleton className="h-6 w-12" /></div>
                    <div className="space-y-2"><Skeleton className="h-3 w-16" /><Skeleton className="h-6 w-12" /></div>
                </div>
            </div>
            <Skeleton className="h-8 w-full rounded-lg mt-4" />
        </div>
    )
}

function CityCardSkeleton() {
    return (
        <div className={cn("", GLASS_CARD)}>
            <div className="p-4 pb-3 flex items-center gap-3">
                <Skeleton className="w-10 h-10 rounded-lg" />
                <Skeleton className="h-5 w-24" />
            </div>
            <div className={cn("mx-4", GLASS_DIVIDER)} />
            <div className="p-4 pt-3 flex gap-2">
                <div className="flex-1 space-y-2"><Skeleton className="h-3 w-16 mx-auto" /><Skeleton className="h-6 w-12 mx-auto" /></div>
                <div className="flex-1 space-y-2"><Skeleton className="h-3 w-16 mx-auto" /><Skeleton className="h-6 w-12 mx-auto" /></div>
            </div>
            <Skeleton className="mx-4 mb-3 mt-1 h-8 rounded-xl" />
        </div>
    )
}

function ErrorState({ message, onRetry }: { message: string; onRetry: () => void }) {
    return (
        <div className="flex flex-col items-center justify-center min-h-[400px] border border-dashed rounded-[2rem] bg-accent/20 text-center p-8 mt-6 z-10 relative">
            <AlertCircle className="h-12 w-12 text-destructive mb-4" />
            <p className="text-xl font-bold text-foreground mb-2">Something went wrong</p>
            <p className="text-muted-foreground mb-6 max-w-sm">{message}</p>
            <button onClick={onRetry} className="flex items-center gap-2 px-4 py-2 bg-background border rounded-xl shadow-sm hover:bg-muted font-medium transition-colors">
                <RefreshCw className="h-4 w-4" /> Try Again
            </button>
        </div>
    )
}

function NoWorkspaceState() {
    return (
        <div className="flex flex-col items-center justify-center min-h-[400px] border border-dashed rounded-[2rem] bg-accent/20 text-center p-8 mt-6 z-10 relative">
            <MapPin className="h-12 w-12 text-muted-foreground/50 mb-4" />
            <p className="text-xl font-bold text-foreground mb-2">No workspace selected</p>
            <p className="text-muted-foreground mb-4 max-w-sm">Please select a workspace from the sidebar to view visibility data.</p>
        </div>
    )
}

// ============================================================================
// MAIN PAGE COMPONENT
// ============================================================================

export function VisibilityPage() {
    const {
        overview, categories, cities,
        categoryDrilldown, cityDrilldown,
        categoryGeoSnapshot,
        cityGeoSnapshot, keywordGeoSnapshot,
        isLoading, isLoadingDrilldown, isLoadingKeywordGeo, error,
        viewMode, selectedCategory, selectedKeyword,
        filters, setFilters,
        selectCategory, openCategoryDrilldown, openAllCitiesDrilldown, openCityDrilldown, selectKeyword, closeDrilldown,
        refresh, workspaceId
    } = useVisibility()

    const sidebarOpen = viewMode !== 'overview'
    const isCityDrilldown = viewMode === 'city-drilldown'
    const activeDrilldownData = isCityDrilldown ? cityDrilldown : categoryDrilldown

    const categoryFilterOptions = useMemo(() => {
        return Array.from(
            new Set(
                (categories || [])
                    .map((category) => category.name?.trim())
                    .filter((name): name is string => Boolean(name))
            )
        )
            .sort((a, b) => a.localeCompare(b))
            .map((name) => ({ value: name, label: name }));
    }, [categories]);

    // Inject filters into the primary TopBar
    const { setFilterContent } = useTopBar()

    // Filter handlers
    const handlePlatformChange = useCallback((value: string) => {
        setFilters({ platform: value as 'all' | 'blinkit' | 'zepto' | 'instamart' | 'bbnow' | 'flipkart' })
    }, [setFilters])

    const handleDateRangeChange = useCallback((range: DateRange | undefined) => {
        if (range?.from && range?.to) {
            const customRange: CustomDateRange = {
                startDate: format(range.from, 'yyyy-MM-dd'),
                endDate: format(range.to, 'yyyy-MM-dd'),
            }
            setFilters({ dateRange: customRange })
        }
    }, [setFilters])

    const handleCategoryChange = useCallback((value: string) => {
        setFilters({ category: value })
    }, [setFilters])

    // Convert filters.dateRange to DateRange for DateRangePicker
    const dateRangeValue: DateRange | undefined = useMemo(() => {
        if (!filters.dateRange || typeof filters.dateRange === 'string') return undefined
        return {
            from: new Date(filters.dateRange.startDate),
            to: new Date(filters.dateRange.endDate),
        }
    }, [filters.dateRange])

    // Push filters into the primary TopBar (compact horizontal pills)
    useEffect(() => {
        setFilterContent(
            <div className="flex items-center gap-2">
                <TopBarFilterDropdown
                    value={filters.platform || 'all'}
                    options={PLATFORM_OPTIONS}
                    onChange={handlePlatformChange}
                />
                <div className="w-px h-4 bg-border/60" />
                <DateRangePicker
                    value={dateRangeValue}
                    onChange={handleDateRangeChange}
                />
                <div className="w-px h-4 bg-border/60" />
                <TopBarFilterDropdown
                    value={filters.category || 'All Categories'}
                    options={categoryFilterOptions}
                    onChange={handleCategoryChange}
                />
            </div>
        )
        return () => setFilterContent(null)
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [filters.platform, dateRangeValue, filters.category, categoryFilterOptions])

    const handleCategorySelect = (category: CategoryVisibility) => {
        selectCategory(category)
    }

    const handleCategoryDrilldown = (category: CategoryVisibility) => {
        openCategoryDrilldown(category)
    }

    const handleCityDrilldown = (city: CityVisibility) => {
        openCityDrilldown(city)
    }

    const handleAllCitiesFilter = () => {
        setFilters({ cityId: undefined })
        closeDrilldown()
    }

    const handleAllCitiesDrilldown = () => {
        openAllCitiesDrilldown()
    }

    const handleSidebarClose = () => {
        closeDrilldown()
    }

    if (!workspaceId && !isLoading) {
        return <div className="p-6"><NoWorkspaceState /></div>
    }

    return (
        <div className="min-h-screen bg-[#FDFDFD] dark:bg-[#0A0A0A] text-foreground font-sans relative overflow-x-hidden">
            {/* Background SVG Pattern */}
            <div className="absolute top-0 inset-x-0 h-96 bg-gradient-to-b from-primary/5 to-transparent pointer-events-none z-0" />


            <div className="max-w-[1600px] mx-auto p-4 md:p-6 relative z-10 flex flex-col gap-6 sm:gap-8">

                {error && error !== 'No workspace selected' ? (
                    <ErrorState message={error} onRetry={refresh} />
                ) : (
                    <>
                        {/* High-Level SOV Metrics */}
                        <div className="flex flex-col sm:flex-row gap-4 sm:gap-6">
                            {isLoading || !overview ? (
                                <>
                                    <div className={cn("flex-1 p-6 h-[140px]", GLASS_CARD)}>
                                        <Skeleton className="h-6 w-32 mb-4" />
                                        <Skeleton className="h-10 w-24" />
                                    </div>
                                    <div className={cn("flex-1 p-6 h-[140px]", GLASS_CARD)}>
                                        <Skeleton className="h-6 w-32 mb-4" />
                                        <Skeleton className="h-10 w-24" />
                                    </div>
                                    <div className={cn("flex-1 p-6 h-[140px]", GLASS_CARD)}>
                                        <Skeleton className="h-6 w-32 mb-4" />
                                        <Skeleton className="h-10 w-24" />
                                    </div>
                                </>
                            ) : (
                                <>
                                    <SOVMetricCard title="Overall SOV" metric={overview.overallSOV} type="overall" />
                                    <SOVMetricCard title="Overall Organic SOV" metric={overview.organicSOV} type="organic" />
                                    <SOVMetricCard title="Ad SOV" metric={overview.adSOV} type="ad" />
                                </>
                            )}
                        </div>

                        {/* Categories Section - Slider */}
                        <section>
                            <div className="flex items-center gap-2 mb-4 px-1">
                                <h3 className="text-sm font-bold uppercase tracking-widest text-muted-foreground">Categories Overview</h3>
                                {selectedCategory && (
                                    <span className="text-xs font-semibold text-primary bg-primary/10 px-2.5 py-0.5 rounded-full">
                                        Filtering: {selectedCategory.name}
                                    </span>
                                )}
                            </div>

                            {isLoading ? (
                                <div className="flex gap-4 py-2 px-1">
                                    {Array.from({ length: 4 }).map((_, i) => <CategoryCardSkeleton key={i} />)}
                                </div>
                            ) : categories.length > 0 ? (
                                <SliderCarousel className="gap-4 py-2 px-1">
                                    {categories.map((cat) => (
                                        <div key={cat.id} className="flex-shrink-0 w-[320px]">
                                            <CategoryCard
                                                category={cat}
                                                isSelected={selectedCategory?.id === cat.id || (!selectedCategory && (cat.name === 'All Categories' || cat.name === 'All'))}
                                                onSelect={() => handleCategorySelect(cat)}
                                                onDrilldown={() => handleCategoryDrilldown(cat)}
                                            />
                                        </div>
                                    ))}
                                </SliderCarousel>
                            ) : (
                                <div className={cn("p-10 flex flex-col items-center justify-center text-muted-foreground", GLASS_CARD)}>
                                    <BarChart3 className="h-10 w-10 mb-3 opacity-30" />
                                    <p className="font-medium">No category data available</p>
                                </div>
                            )}
                        </section>

                        {/* Cities Section - Grid */}
                        <section className="pb-20">
                            <div className="flex items-center gap-2 mb-4 px-1">
                                <h3 className="text-sm font-bold uppercase tracking-widest text-muted-foreground">City Performance</h3>
                                {selectedCategory && (
                                    <span className="text-xs text-muted-foreground">
                                        — showing SOV for <span className="font-semibold text-foreground">{selectedCategory.name}</span>
                                    </span>
                                )}
                            </div>

                            {isLoading ? (
                                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 2xl:grid-cols-5 gap-4 px-1">
                                    {Array.from({ length: 10 }).map((_, i) => <CityCardSkeleton key={i} />)}
                                </div>
                            ) : cities.length > 0 ? (
                                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 2xl:grid-cols-5 gap-4 px-1">
                                    {/* All Cities summary card — pinned first */}
                                    {overview && (
                                        <AllCitiesCard
                                            overallSOV={overview.overallSOV.value}
                                            adSOV={overview.adSOV.value}
                                            overallChange={overview.overallSOV.change}
                                            adChange={overview.adSOV.change}
                                            isSelected={!filters.cityId}
                                            onChange={handleAllCitiesFilter}
                                            onDrilldown={handleAllCitiesDrilldown}
                                        />
                                    )}
                                    {cities.map((city) => (
                                        <CityCard key={city.id} city={city} onDrilldown={() => handleCityDrilldown(city)} />
                                    ))}
                                </div>
                            ) : (
                                <div className={cn("p-10 flex flex-col items-center justify-center text-muted-foreground", GLASS_CARD)}>
                                    <MapPin className="h-10 w-10 mb-3 opacity-30" />
                                    <p className="font-medium">No city data available</p>
                                </div>
                            )}
                        </section>
                    </>
                )}
            </div>

            {/* Drilldown Sidebar */}
            <VisibilityDrilldownSidebar
                isOpen={sidebarOpen}
                drilldownData={activeDrilldownData}
                cityGeoData={cityGeoSnapshot}
                categoryGeoData={categoryGeoSnapshot}
                keywordGeoData={keywordGeoSnapshot}
                isLoading={isLoadingDrilldown}
                isLoadingKeywordGeo={isLoadingKeywordGeo}
                drilldownMode={isCityDrilldown ? 'city' : 'category'}
                selectedCategoryName={selectedCategory?.name ?? filters.category}
                selectedKeyword={selectedKeyword}
                onKeywordSelect={selectKeyword}
                onClose={handleSidebarClose}
            />
        </div>
    )
}
